import { toast } from '@vwt/ui';
import i18n from './i18n.js';
import { isApiError } from './api.js';

/** User-facing message for an API error; violations from guards are shown verbatim. */
export function errorMessage(error: unknown): string {
  if (!isApiError(error)) return i18n.t('errors.generic', { correlationId: '—' });
  const env = error.envelope;
  switch (error.code) {
    case 'NOT_FOUND':
      return i18n.t('errors.notFound');
    case 'FORBIDDEN':
    case 'CSRF_REJECTED':
      return env?.error.message ?? i18n.t('errors.forbidden');
    case 'UNAUTHENTICATED':
    case 'SESSION_EXPIRED':
      return i18n.t('errors.unauthenticated');
    case 'VERSION_CONFLICT':
    case 'IDEMPOTENCY_CONFLICT':
      return i18n.t('errors.conflict');
    case 'STALE_GENERATION':
      return i18n.t('errors.staleGeneration');
    case 'RATE_LIMITED':
      return i18n.t('errors.rateLimited');
    case 'CAPABILITY_UNAVAILABLE':
    case 'CONNECTOR_UNAVAILABLE':
      return env?.error.message ?? i18n.t('errors.unavailable');
    case 'GUARD_VIOLATION':
    case 'INVALID_STATE':
      return env?.error.details?.violations?.map((v) => v.message).join(' ') || env?.error.message || i18n.t('errors.validation');
    case 'VALIDATION_FAILED':
      return env?.error.details?.fields?.map((f) => `${f.path}: ${f.message}`).join('; ') || i18n.t('errors.validation');
    default:
      return i18n.t('errors.generic', { correlationId: env?.meta.correlationId ?? '—' });
  }
}

export function toastError(error: unknown) {
  toast.error(errorMessage(error));
}

/** Field-level messages from a VALIDATION_FAILED error, keyed by path. */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!isApiError(error) || error.code !== 'VALIDATION_FAILED') return {};
  return Object.fromEntries((error.envelope?.error.details?.fields ?? []).map((f) => [f.path, f.message]));
}
